import type { KeyboardEvent } from "react";
import type { SubtaskId, Task, TaskId } from "../../../../shared/types";
import type {
  SwipeState,
  SwipeHandlers,
} from "../../../../shared/hooks/use-swipe";
import type { LongPressHandlers } from "../../../../shared/hooks/use-long-press";
import { TaskItemHeader } from "./TaskItemHeader";
import { TaskItemBadges } from "./TaskItemBadges";
import { TaskItemActions } from "./TaskItemActions";
import { TaskItemDetails } from "./TaskItemDetails";

interface TaskItemRowProps {
  readonly task: Task;
  readonly swipe: SwipeState & SwipeHandlers;
  readonly longPress: LongPressHandlers;
  readonly selectable: boolean;
  readonly selected: boolean;
  readonly isEditing: boolean;
  readonly editValue: string;
  readonly isDone: boolean;
  readonly isExpanded: boolean;
  readonly inputRef: React.RefObject<HTMLInputElement | null>;
  readonly showCategory?: boolean;
  readonly onCycleStatus: (id: TaskId) => void;
  readonly onEditValueChange: (value: string) => void;
  readonly onStartEdit: () => void;
  readonly onCommitEdit: () => void;
  readonly onKeyDown: (event: KeyboardEvent) => void;
  readonly onSelect?: (id: TaskId) => void;
  readonly onToggleExpand: () => void;
  readonly onDelete: () => void;
  readonly onUpdate: (
    id: TaskId,
    updates: Partial<
      Pick<Task, "title" | "priority" | "notes" | "tags" | "dueDate">
    >,
  ) => void;
  readonly onAddSubtask: (taskId: TaskId, title: string) => void;
  readonly onToggleSubtask: (taskId: TaskId, subtaskId: SubtaskId) => void;
  readonly onDeleteSubtask: (taskId: TaskId, subtaskId: SubtaskId) => void;
}

export function TaskItemRow({
  task,
  swipe,
  longPress,
  selectable,
  selected,
  isEditing,
  editValue,
  isDone,
  isExpanded,
  inputRef,
  showCategory,
  onCycleStatus,
  onEditValueChange,
  onStartEdit,
  onCommitEdit,
  onKeyDown,
  onSelect,
  onToggleExpand,
  onDelete,
  onUpdate,
  onAddSubtask,
  onToggleSubtask,
  onDeleteSubtask,
}: TaskItemRowProps) {
  const handleTouchStart = (e: React.TouchEvent) => {
    swipe.onTouchStart(e);
    longPress.onTouchStart(e);
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    swipe.onTouchMove(e);
    longPress.onTouchMove(e);
  };
  const handleTouchEnd = () => {
    swipe.onTouchEnd();
    longPress.onTouchEnd();
  };

  return (
    <>
      <div
        className="group flex items-center gap-2 md:gap-3 pl-3 md:pl-4 pr-2 md:pr-3 py-1.5 md:py-3 relative z-10 transition-opacity"
        style={{
          opacity: swipe.swiping ? 0.7 : 1,
          transform: `translateX(${Math.max(swipe.swipeX * 0.1, -10)}px)`,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {selectable && (
          <label className="flex items-center cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onSelect?.(task.id)}
              className="sr-only"
              aria-label={`Sélectionner: ${task.title}`}
            />
            <div className={`w-4 h-4 rounded-full border-2 transition-all flex items-center justify-center
              ${selected
                ? "bg-accent border-accent"
                : "border-border hover:border-accent/50"
              }`}>
              {selected && (
                <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
          </label>
        )}

        <TaskItemHeader
          task={task}
          isEditing={isEditing}
          editValue={editValue}
          isDone={isDone}
          onCycleStatus={onCycleStatus}
          onEditValueChange={onEditValueChange}
          onStartEdit={onStartEdit}
          onCommitEdit={onCommitEdit}
          onKeyDown={onKeyDown}
          inputRef={inputRef}
        />

        <TaskItemBadges task={task} showCategory={showCategory} />
        <TaskItemActions
          isExpanded={isExpanded}
          onToggleExpand={onToggleExpand}
          onDelete={onDelete}
        />
      </div>

      {isExpanded && (
        <TaskItemDetails
          task={task}
          onUpdate={onUpdate}
          onAddSubtask={onAddSubtask}
          onToggleSubtask={onToggleSubtask}
          onDeleteSubtask={onDeleteSubtask}
        />
      )}
    </>
  );
}
